// X96 (Grant 2026-10-21: "Why not allow the user to make it bookable from this screen? … a lot of your work doesn't
// take into account the user experience making them jump around screens"): the fixes the Bookings admin offers in
// place instead of sending the admin to another page. Pure words and request bodies, so they're tested without a DOM.

/**
 * What the Setup page (playingAreas/index.vue) saves for a playing area: the whole detail it loaded, with the
 * description always sent (an emptied field clears it), the order a number and the sports as a list. ONE body for
 * the Setup page's form and for every in-place save, so a quick "Make it bookable" never drops a field the form keeps.
 */
export function playingAreaSaveBody(area) {
  return {
    ...area,
    // X53: always sent, so an emptied field clears the stored line (the API keeps it when the field is missing).
    description: area.description ?? '',
    sort: area.sort !== null && area.sort !== '' && area.sort !== undefined ? parseInt(area.sort) : 0,
    sportIds: Array.isArray(area.sportIds) ? area.sportIds : (area.sportId ? [area.sportId] : []),
  };
}

/** The Setup page's body for this area with only Can be booked changed (and the yes to a preview when given). */
export function bookableBody(detail, canBeBooked, confirm = false) {
  const body = {...playingAreaSaveBody(detail), canBeBooked: !!canBeBooked};
  if (confirm) body.confirm = true;
  return body;
}

/**
 * The walk-up area's dialog on the admin calendar. An admin who can edit playing areas is offered the switch right
 * there; anyone else is told what the area is, with no page to go to.
 */
export function walkUpWords({areaName, rulesName, canEdit}) {
  const name = areaName || 'This playing area';
  if (!canEdit) {
    return {
      title: `${name} is open play`,
      body: `${name} is a walk-up playing area: always open play, first come, first served. Nothing is booked on it.`,
      confirm: null,
      cancel: 'OK',
    };
  }
  const rules = rulesName ? `the ${rulesName} booking rules'` : "its booking rules'";
  return {
    title: `Take bookings on ${name}?`,
    body: `${name} is always open play now. Make it bookable to use ${rules} hours, prices and limits.`,
    confirm: 'Make it bookable',
    cancel: 'Keep it open play',
  };
}

/**
 * Make a walk-up area bookable in place, then carry on: reload the calendar so its slots appear and, when the admin
 * had pressed or dragged across it, open the choice for that selection. Every side effect is passed in.
 *   getDetail(id) → the playing area as the Setup page loads it; update(id, body) → the Setup page's save;
 *   reload() → the calendar reloaded; carryOn(selection) → the choice dialog for the selection (null: nothing to carry).
 * Returns {ok, error}.
 */
export async function makeBookableInPlace({areaId, selection = null, getDetail, update, reload, carryOn}) {
  try {
    const detail = await getDetail(areaId);
    await update(areaId, bookableBody(detail, true));
  } catch (e) {
    return {ok: false, error: e};
  }
  await reload();
  if (selection && carryOn) await carryOn(selection);
  return {ok: true, error: null};
}

/** The toast after a playing area's switch is changed in place. */
export function bookableToast(areaName, canBeBooked) {
  return canBeBooked ? `${areaName} can be booked now.` : `${areaName} is walk-up now: always open play.`;
}

/**
 * The calendar's empty states, each with the fix it offers in place: `action` is the button the calendar shows
 * ('switchOn' → the page's own switch-on dialog, 'bookable' → choose the playing areas that can be booked,
 * 'addArea' → the Setup page's Add a playing area dialog, the one big edit that lives there) or null.
 */
export function calendarEmptyState({featureEnabled, bookingsEnabled, areasTotal}) {
  if (featureEnabled === false) {
    return {line: "Bookings are off for this club, so the calendar is empty. Ask Tap Leagues to enable playing area bookings; your playing areas and matches work as they do today.", action: null, button: null};
  }
  if (bookingsEnabled === false) return {line: 'Bookings are off, so the calendar is empty.', action: 'switchOn', button: 'Switch bookings on'};
  if ((areasTotal ?? 0) > 0) return {line: 'None of your playing areas take bookings yet.', action: 'bookable', button: 'Choose which can be booked'};
  return {line: 'No playing areas yet. Add the courts, tables or boards players book.', action: 'addArea', button: 'Add a playing area'};
}

/** The Setup page's own Add a playing area dialog (AdminEntityList opens it from ?action=create). */
export function addAreaRoute(clubId) {
  return {name: 'ClubPlayingAreas', params: {clubId}, query: {action: 'create'}};
}
